import React, {useCallback, useEffect, useMemo, useState} from "react";
import {AuthorService} from "../../../services/author.service";
import {Formik, Form} from "formik";
import {Grid} from "@material-ui/core";
import AddBookPrevious from "../add-book/add-book-previous";
import {EditProfileSocialMediaLinks} from "./edit-profile-social-media-links";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store/root-reducer";
import {StyledInput} from "../../form/input";
import {StyledSelect} from "../../form/select";
import {EditProfileAvatar} from "./edit-profile-avatar";
import {showSnackSuccess, toggleLoader} from '../../../store/global/global-slice';
import {StyledTextarea} from "../../form/textarea";
import {StyledButton} from "../../ui-base/button";
import * as Yup from "yup";
import {logged, updateNickName} from "../../../store/auth/auth-slice";
import {useHistory} from "react-router-dom";
import {AuthService} from "../../../services/auth.service";
import {RequireInfo} from "../../ui-base/require-info";
import {EditProfilePrizes} from "./edit-profile-prizes";
import styled from "styled-components";
import {Colors} from "../../../theme/colors";
import {useTranslation} from "react-i18next";
import {CountryAutocomplete} from "../../form/country-autocomplete";
import {CountryModel} from "../../../models/country.model";

const EditProfileForm = ({actionAfterSave}) => {
    const [file, setFile] = useState(null);
    const [profile, setProfile] = useState(null);
    const [initialPhoto, setInitialPhoto] = useState(null);
    const [years, setYears] = useState([]);
    const history = useHistory();
    const dispatch = useDispatch();
    const { t } = useTranslation(['author', 'common']);


    const countries: CountryModel[] = useSelector(
		(state: RootState) => state.countries.countries
	);

    const EditFormSchema = useMemo(() => Yup.object().shape({
        first_name: Yup.string().required(t('editProfile.form.errors.firstNameRequired')),
        last_name: Yup.string().required(t('editProfile.form.errors.lastNameRequired')),
        country: Yup.object().shape({
            iso: Yup.string().required(t('editProfile.form.errors.countryRequired')),
        }).nullable().required(t('editProfile.form.errors.countryRequired')),
        links: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required(),
                url: Yup.string().required()
            })
        ),
        previousBooks: Yup.array().of(
            Yup.object().shape({
                isbn: Yup.string().required()
            })
        ),
    }), [t]);


    const handleFile = (files: File[]) => {
        setFile(files[0]);
    }

    const formInitialValues = useMemo(() =>
        ({
            nickname: '',
            first_name: '',
            last_name: '',
            birthYear: null,
            residencePlace: '',
            country: null,
            phone: '',
            bio: '',
            previousBooks: [],
            links: [],
            prizes: []
        }), []
    );

    const formSubmit = async (values) => {
        dispatch(toggleLoader(true));
        const res = await AuthorService.updateProfile(
            profile.id,
            values.nickname,
            values.first_name,
            values.last_name,
            values.birthYear,
            values.residencePlace,
            values.country,
            values.phone,
            values.bio,
            values.links,
            values.previousBooks,
            values.prizes
        );

        if (res.status) {
            if (file) {
                const fileUploaded = await AuthorService.updateAvatar(file, res.data.id);

                if (fileUploaded) {
                    AuthService.authMe().then((res) => {
                        if (res) {
                            dispatch(logged(res));
                        }

                        actionAfterSaved();
                    });
                }
            } else {
                const authMeResponse = await AuthService.authMe();
                if (authMeResponse) {
                    dispatch(logged(authMeResponse)); // Update the auth state with new data
                }
                actionAfterSaved();
            }

            dispatch(updateNickName(values.nickname));
        } else {
            dispatch(toggleLoader(false));
        }
    }

    const actionAfterSaved = () => {
        if (actionAfterSave) {
            actionAfterSave();
        } else {
            dispatch(showSnackSuccess(t('editProfile.saved')));
            history.push(`/author/profile/${profile.id}`);
        }

        dispatch(toggleLoader(false));
    }

    const loadProfile = useCallback(async () => {
        const profile = await AuthorService.getProfile();

        if (profile) {
            if (profile.photo && profile.photo.file) {
                setInitialPhoto(profile.photo.file);
            }

            formInitialValues.nickname = profile.nickname || '';
            formInitialValues.first_name = profile.first_name === '-' ? '' : profile.first_name || '';
            formInitialValues.last_name = profile.last_name === '-' ? '' : profile.last_name || '';
            formInitialValues.phone = profile.phone || '';
            formInitialValues.bio = profile.bio || '';
            formInitialValues.birthYear = profile.birth_year || '';
            formInitialValues.residencePlace = profile.residence_place || '';
            formInitialValues.country = profile?.country ? countries.find((country) => country.iso === profile?.country) : null;
            formInitialValues.links = profile.external_links || [];
            formInitialValues.previousBooks = profile.previous_books || [];
            formInitialValues.prizes = profile.author_prizes || [];

            setProfile(profile);
            dispatch(toggleLoader(false));
        }
    }, [dispatch, formInitialValues])

    useEffect(() => {
        dispatch(toggleLoader(true));

        const year_start = 1950;
        const year_end = new Date().getFullYear();
        const tmpYears = [];
        for (let i = 0; i <= (year_end - year_start); i++) {
            tmpYears.push(year_start + i);
        }

        setYears(tmpYears);

        loadProfile();
    }, [dispatch, loadProfile]);

    return (
        <Formik
            initialValues={formInitialValues}
            onSubmit={(values) => {
                formSubmit(values);
            }}
            validationSchema={EditFormSchema}
        >
            {({values, touched, errors, handleChange, handleBlur, isValid, setFieldValue}) => (
                <Form style={{width: '100%'}}>
                    <Grid container direction="row" justifyContent="flex-end" spacing={2}>
                        <Grid item>
                            <StyledButton type="submit">
                                {t('common:save')}
                            </StyledButton>
                        </Grid>
                    </Grid>

                    <Grid container spacing={4} direction="row">
                        <Grid container item sm={12} md={6} direction="column" spacing={3}>
                            <EditProfileAvatar
                                initialImage={initialPhoto}
                                handleImage={handleFile}
                            />

                            <InputNameWrapper item style={{ display: 'none' }}>
                                <StyledInput
                                    id="nickname"
                                    label={t('editProfile.form.nickname') + " *"}
                                    onChange={handleChange}
                                    value={values.nickname}
                                    error={errors.nickname && touched.nickname}
                                />
                            </InputNameWrapper>

                            {/* Display the message in red if first_name or last_name is empty, null, or '-' */}
                            {(!values.first_name || values.first_name === '-' || !values.last_name || values.last_name === '-' || !values.country || values.country === ' ') && (
                                <div style={{ color: 'red' }}>
                                    {t('editProfile.form.errors.missingProfileDetails')}
                                </div>
                            )}

                            <InputNameWrapper item>
                                <StyledInput
                                    id="first_name"
                                    label={t('editProfile.form.firstName') + " *"}
                                    onChange={handleChange}
                                    value={values.first_name}
                                    error={errors.first_name && touched.first_name}
                                />
                            </InputNameWrapper>

                            {errors.first_name && touched.first_name && (
                                <div style={{ color: 'red' }}>{errors.first_name}</div>
                            )}

                            <InputNameWrapper item>
                                <StyledInput
                                    id="last_name"
                                    label={t('editProfile.form.lastName') + " *"}
                                    onChange={handleChange}
                                    value={values.last_name}
                                    error={errors.last_name && touched.last_name}
                                />
                            </InputNameWrapper>

                            {errors.last_name && touched.last_name && (
                                <div style={{ color: 'red' }}>{errors.last_name}</div>
                            )}

                            <Grid item>
                                <CountryAutocomplete
                                    values={values}
                                    multiple={false}
                                    setFieldValue={setFieldValue}
                                    fieldName={'country'}
                                    label={t('editProfile.form.country') + "*"}
                                    error={!!(errors.country && touched.country)}
                                />
                            </Grid>

                            {errors.country && touched.country && (
                                <div style={{ color: 'red' }}>{errors.country}</div>
                            )}

                            <Grid item>
                                <StyledInput
                                    id="residencePlace"
                                    label={t('editProfile.form.residencePlace')}
                                    onChange={handleChange}
                                    value={values.residencePlace}
                                />
                            </Grid>

                            <Grid item>
                                <StyledSelect
                                    id="birthYear"
                                    label={t('editProfile.form.birthYear')}
                                    onChange={handleChange}
                                    value={values.birthYear}
                                    options={years}
                                />
                            </Grid>

                            <Grid item>
                                <StyledInput
                                    id="phone"
                                    label={t('editProfile.form.phone')}
                                    onChange={handleChange}
                                    value={values.phone}
                                />
                            </Grid>

                            <Grid item>
                                <StyledTextarea
                                    id="bio"
                                    label={t('editProfile.form.bio')}
                                    onChange={handleChange}
                                    value={values.bio}
                                    rows={10}
                                />
                            </Grid>
                        </Grid>

                        <Grid container item sm={12} md={6} direction="column" spacing={3}>
                            <Grid item>
                                <EditProfileSocialMediaLinks values={values} fieldName={'links'} handleChange={handleChange} errors={errors} touched={touched}/>
                            </Grid>

                            <Grid item>
                                <AddBookPrevious values={values} fieldName={'previousBooks'} handleChange={handleChange} errors={errors} touched={touched} setFieldValue={setFieldValue}/>
                            </Grid>

                            <Grid item>
                                <EditProfilePrizes values={values} fieldName={'prizes'} handleChange={handleChange} errors={errors} touched={touched}/>
                            </Grid>
                        </Grid>
                    </Grid>

                    <RequireInfo></RequireInfo>
                </Form>
            )}
        </Formik>
    );
}

export default EditProfileForm;

const InputNameWrapper = styled(Grid)`
  div label {
    font-size: 1.1rem;
    font-weight: bold;
  }
`;
